import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import { ExcelButton } from "../../controls/buttons/ExcelButton";
import { NotificationSnackBar } from "../../controls/snackbars/notification-snackbar";
import { isMobileDevice } from "../../utils/responsive-utils";
import { useResize } from "../../utils/useResize";
import {
  Autocomplete,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBackRounded, ArrowForwardRounded } from "@mui/icons-material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { GridApi } from "ag-grid-community";
import { get } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { exportSingleGridToExcel } from "../meltshop/dashboard/utils/exportSingleGridToExcel";
import {
  ExportGridRef,
  ProcessByShift,
} from "../meltshop/models/ProcessByShift";
import { ProductionReportTable } from "./components/ProductionReportTable";
import { useProcessConfig } from "./hooks/useProductionConfig";
import { getProcessesByDateRange } from "./repositories/ProductionReportRepository";

const DAYS_OPTIONS = [
  { value: 1, label: "1 day" },
  { value: 3, label: "3 days" },
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
];

const moment = getMomentTz();

interface ProcessesReportPageProps {
  processes?: ProcessByShift[];
  isLoading?: boolean;
  error?: string;
}

export const ProductionReportPage = ({
  processes: propProcesses,
  isLoading: propIsLoading,
  error: propError,
}: ProcessesReportPageProps = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const assetId = searchParams.get("assetId");
  const assetIdNumber = Number(assetId);
  const config = useProcessConfig(assetIdNumber);

  if (!config) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ p: 4 }}>
        <Typography variant="h6" color="error">
          Invalid asset ID: {assetId}
        </Typography>
      </Grid>
    );
  }

  const [processesByDate, setProcessesByDate] = useState<ProcessByShift[]>(
    propProcesses || [],
  );
  const [isLoadingGrid, setIsLoadingGrid] = useState(propIsLoading || false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(propError || "");
  const [notificationMessage, setNotificationMessage] = useState("");
  const { width } = useResize();
  const isMobile = isMobileDevice(width);

  // Filter states
  const [selectedDate, setSelectedDate] = useState(() => {
    const dateParam = moment(searchParams.get("date"), "YYYY-MM-DD", true);
    return dateParam.isValid() ? dateParam : moment();
  });
  const [selectedTime, setSelectedTime] = useState(() => {
    const timeParam = moment(searchParams.get("time"), "HH:mm", true);
    return timeParam.isValid()
      ? timeParam
      : moment().hour(8).minute(0).second(0).millisecond(0);
  });
  const [selectedDays, setSelectedDays] = useState(() => {
    const daysParam = Number(searchParams.get("days"));
    return DAYS_OPTIONS.some((opt) => opt.value === daysParam) ? daysParam : 1;
  });

  useEffect(() => {
    setSearchParams(
      (params) => {
        params.set("date", selectedDate.format("YYYY-MM-DD"));
        params.set("time", selectedTime.format("HH:mm"));
        params.set("days", String(selectedDays));
        return params;
      },
      { replace: true },
    );
  }, [selectedDate, selectedTime, selectedDays, setSearchParams]);

  // Grid API for export functionality
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const handleExportToExcel = useCallback(() => {
    if (!gridApi) {
      setNotificationMessage("Please wait until the table is loaded");
      return;
    }

    setIsExporting(true);

    const gridRef: ExportGridRef = {
      gridApi,
      sheetName: `${config.assetName} Processes Report`,
    };

    try {
      const exportDate = moment(selectedDate)
        .hour(selectedTime.hour())
        .minute(selectedTime.minute())
        .second(0)
        .toDate();
      exportSingleGridToExcel(gridRef, exportDate);
      setNotificationMessage("Export to Excel completed successfully");
    } catch (error) {
      setErrorMessage("Failed to export to Excel");
    } finally {
      setIsExporting(false);
    }
  }, [gridApi, selectedDate, selectedTime, config.assetName]);

  const getDataFromAPI = useCallback(async () => {
    if (!propProcesses) {
      setIsLoadingGrid(true);

      // Combine selected date with selected time
      const startDateTime = selectedDate
        .clone()
        .hour(selectedTime.hour())
        .minute(selectedTime.minute())
        .second(0)
        .millisecond(0);

      const startDateTimeUTC = startDateTime.utc().toDate();

      const endDateTime = new Date(
        startDateTimeUTC.getTime() + selectedDays * 24 * 60 * 60 * 1000,
      );

      const resp = await getProcessesByDateRange(
        startDateTimeUTC,
        endDateTime,
        assetIdNumber,
      );
      if (resp.ok) {
        setProcessesByDate(get(resp, "data.tables[0].rows", []));
      } else {
        setErrorMessage(resp.message);
      }
      setIsLoadingGrid(false);
    }
  }, [propProcesses, selectedDate, selectedTime, selectedDays, assetIdNumber]);

  const handleDateChange = (newValue: moment.Moment | null) => {
    const dateToSet = newValue || moment();
    if (!selectedDate.isSame(dateToSet, "day")) {
      setSelectedDate(dateToSet);
    }
  };

  const handleTimeChange = (newValue: moment.Moment | null) => {
    const timeToSet =
      newValue || moment().hour(8).minute(0).second(0).millisecond(0);
    if (!selectedTime.isSame(timeToSet, "minute")) {
      setSelectedTime(timeToSet);
    }
  };

  const handleDaysChange = (newDays: number) => {
    if (newDays !== selectedDays) {
      setSelectedDays(newDays);
    }
  };

  const handleNavigate = (direction: "prev" | "next") => {
    const daysToMove = direction === "next" ? selectedDays : -selectedDays;
    const newDate = selectedDate.clone().add(daysToMove, "days");
    setSelectedDate(newDate);
  };

  const isToday = selectedDate.isSame(moment(), "day");

  useEffect(() => {
    getDataFromAPI();
  }, [getDataFromAPI]);

  return (
    <>
      <HelmetDexteel
        title={`${selectedDate.toDate().toLocaleDateString()} - ${
          config.title
        }`}
      />
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {config.title}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }} elevation={2}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid
                item
                container
                xs={12}
                md={6}
                spacing={1}
                alignItems="center"
              >
                <Grid item>
                  <IconButton
                    sx={{ p: 1 }}
                    onClick={() => handleNavigate("prev")}
                    title={`Go back ${selectedDays} ${
                      selectedDays === 1 ? "day" : "days"
                    }`}
                  >
                    <ArrowBackRounded />
                  </IconButton>
                </Grid>

                <Grid item xs={10} sm={3} md={2}>
                  <DatePicker
                    label="Date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        size: "small",
                        InputLabelProps: {
                          shrink: true,
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={6} sm={3} md={2}>
                  <TimePicker
                    label="Time"
                    value={selectedTime}
                    onChange={handleTimeChange}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        size: "small",
                        InputLabelProps: {
                          shrink: true,
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={4} sm={3} md={2}>
                  <Autocomplete
                    options={DAYS_OPTIONS}
                    getOptionLabel={(option) => option.label}
                    value={DAYS_OPTIONS.find(
                      (opt) => opt.value === selectedDays,
                    )}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        handleDaysChange(newValue.value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Period"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    )}
                    disableClearable
                  />
                </Grid>

                <Grid item>
                  <IconButton
                    sx={{ p: 1 }}
                    onClick={() => handleNavigate("next")}
                    disabled={isToday}
                    title={`Go forward ${selectedDays} ${
                      selectedDays === 1 ? "day" : "days"
                    }`}
                  >
                    <ArrowForwardRounded />
                  </IconButton>
                </Grid>
              </Grid>

              {!isMobile && (
                <Grid item xs={12} md={2} container justifyContent="flex-end">
                  <ExcelButton
                    handleExportToExcel={handleExportToExcel}
                    disabled={
                      !gridApi ||
                      isLoadingGrid ||
                      isExporting ||
                      processesByDate.length === 0
                    }
                    label={isExporting ? "EXPORTING..." : "EXPORT TO EXCEL"}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <ProductionReportTable
            loadingGrid={isLoadingGrid}
            rows={processesByDate}
            onGridReady={(params) => setGridApi(params.api)}
            refreshData={getDataFromAPI}
            assetId={assetIdNumber}
          />
        </Grid>
      </Grid>
      <ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </>
  );
};
